import * as React from 'react';
import { Check, Copy } from 'lucide-react';
import type { AgentProptreeSettings } from '@proptr/shared';
import { AppShell } from '../components/AppShell.js';
import { Card } from '../components/ui/card.js';
import {
  Field,
  ProptreeFormSkeleton,
  SaveBar,
  SectionHeader,
  Switch,
} from '../components/proptreeForm.js';
import { client } from '../main.js';

type LoadStatus =
  | { kind: 'loading' }
  | { kind: 'ready' }
  | { kind: 'no-proptree' }
  | { kind: 'error'; message: string };

export function SettingsPage() {
  const [status, setStatus] = React.useState<LoadStatus>({ kind: 'loading' });
  const [data, setData] = React.useState<AgentProptreeSettings | null>(null);
  const [original, setOriginal] = React.useState<AgentProptreeSettings | null>(
    null,
  );
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [savedAt, setSavedAt] = React.useState<Date | null>(null);

  React.useEffect(() => {
    let alive = true;
    client.agents
      .getMyProptree()
      .then((s) => {
        if (!alive) return;
        setData(s);
        setOriginal(s);
        setStatus({ kind: 'ready' });
      })
      .catch((err: Error & { code?: string }) => {
        if (!alive) return;
        if (
          err.message?.includes('NO_AGENT_PROPTREE') ||
          /404/.test(err.message)
        ) {
          setStatus({ kind: 'no-proptree' });
        } else {
          setStatus({ kind: 'error', message: err.message });
        }
      });
    return () => {
      alive = false;
    };
  }, []);

  const dirty = React.useMemo(() => {
    if (!data || !original) return false;
    return (
      data.recoveryEmail !== original.recoveryEmail ||
      JSON.stringify(data.notifications) !==
        JSON.stringify(original.notifications)
    );
  }, [data, original]);

  // Send the full settings payload — only recoveryEmail/notifications are
  // editable on this page, but the API endpoint replaces the whole object.
  const handleSave = async () => {
    if (!data || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await client.agents.updateMyProptree({
        alias: data.alias ?? undefined,
        name: data.name ?? undefined,
        recoveryEmail: data.recoveryEmail ?? '',
        social: data.social,
        buttons: data.buttons.map((b) => ({
          id: b.id,
          title: b.title,
          subtitle: b.subtitle,
          url: b.url,
          icon: b.icon,
          image: b.image,
          images: b.images,
          enabled: b.enabled,
          type: b.type,
        })),
        notifications: data.notifications,
      });
      setData(updated);
      setOriginal(updated);
      setSavedAt(new Date());
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell>
      <div className="space-y-8 pb-32">
        <div className="border-b border-[hsl(var(--hairline))] pb-6">
          <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.025em]">
            Settings
          </h1>
          <p className="mt-3 font-mono text-[11px] text-muted-foreground">
            Account recovery and notification preferences.
          </p>
        </div>

        {status.kind === 'loading' && <ProptreeFormSkeleton />}

        {status.kind === 'no-proptree' && (
          <Card className="p-8 text-center">
            <h2 className="font-display text-[24px] font-medium tracking-[-0.02em]">
              No proptree yet
            </h2>
            <p className="mx-auto mt-3 max-w-md font-mono text-[12px] leading-relaxed text-muted-foreground">
              Your manager hasn't created a proptree for you yet. Once they
              do, your settings will appear here.
            </p>
          </Card>
        )}

        {status.kind === 'error' && (
          <Card className="p-6">
            <p className="font-mono text-[12px] text-destructive">
              {status.message}
            </p>
          </Card>
        )}

        {status.kind === 'ready' && data && (
          <>
            <RecoveryEmailCard data={data} onChange={setData} />
            <RecoveryCodeCard data={data} />
            <NotificationsCard data={data} onChange={setData} />
          </>
        )}
      </div>

      {status.kind === 'ready' && (
        <SaveBar
          dirty={dirty}
          saving={saving}
          error={saveError}
          savedAt={savedAt}
          onSave={handleSave}
        />
      )}
    </AppShell>
  );
}

function RecoveryEmailCard({
  data,
  onChange,
}: {
  data: AgentProptreeSettings;
  onChange: (next: AgentProptreeSettings) => void;
}) {
  return (
    <Card className="space-y-6 p-6">
      <SectionHeader
        title="Recovery email"
        description="A second way back into your account if you lose access."
      />
      <Field
        label="Email"
        type="email"
        value={data.recoveryEmail ?? ''}
        onChange={(v) => onChange({ ...data, recoveryEmail: v })}
        placeholder="you@example.com"
      />
    </Card>
  );
}

function RecoveryCodeCard({ data }: { data: AgentProptreeSettings }) {
  const [copied, setCopied] = React.useState(false);
  const code = data.recoveryCode ?? '';
  const onCopy = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <Card className="space-y-6 p-6">
      <SectionHeader
        title="Save your recovery code"
        description="The only way to restore your account if you lose access. Keep it safe."
      />
      <div className="flex items-center gap-3">
        <div className="rounded-md border border-border bg-muted px-4 py-2 font-mono text-[20px] font-semibold tabular-nums text-foreground">
          {code || '—'}
        </div>
        {code && (
          <button
            type="button"
            onClick={onCopy}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 font-mono text-[11px] font-medium uppercase tracking-[0.04em] text-foreground transition-colors hover:bg-muted dark:border-white/10 dark:bg-card-raised"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
            {copied ? 'Copied' : 'Copy'}
          </button>
        )}
      </div>
    </Card>
  );
}

function NotificationsCard({
  data,
  onChange,
}: {
  data: AgentProptreeSettings;
  onChange: (next: AgentProptreeSettings) => void;
}) {
  const set = (key: keyof typeof data.notifications, value: boolean) =>
    onChange({
      ...data,
      notifications: { ...data.notifications, [key]: value },
    });

  const rows: Array<{
    key: keyof typeof data.notifications;
    label: string;
    hint: string;
  }> = [
    {
      key: 'accountCritical',
      label: 'Account critical',
      hint: 'Security and account-recovery alerts. Best to leave on.',
    },
    {
      key: 'reminders',
      label: 'Reminders',
      hint: 'Nudges to keep your proptree fresh.',
    },
    {
      key: 'reports',
      label: 'Reports',
      hint: 'Weekly + monthly performance summaries.',
    },
  ];

  return (
    <Card className="space-y-6 p-6">
      <SectionHeader
        title="Notifications"
        description="Pick which classes of messages we send you."
      />
      <div className="divide-y divide-[hsl(var(--hairline))]">
        {rows.map((r) => (
          <div
            key={r.key}
            className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0"
          >
            <div className="min-w-0">
              <div className="font-sans text-[13px] font-medium">
                {r.label}
              </div>
              <div className="font-mono text-[10px] text-muted-foreground">
                {r.hint}
              </div>
            </div>
            <Switch
              checked={data.notifications[r.key]}
              onChange={(v) => set(r.key, v)}
              ariaLabel={r.label}
            />
          </div>
        ))}
      </div>
    </Card>
  );
}
